import type { NavGroup, NavPage } from "../topNavData"

type Props = {
  groups: NavGroup[]
  active: string
  onSelect: (page: NavPage) => void
  onClose: () => void
}

// Every page at once, dropped down from the top bar over a dimmed page.
// A tinted first column holds Home and My Work with the smaller groups
// stacked under them; then one column each for Planning, Purchase and Push.
// Columns wrap onto more lines when the page is narrow.
// Picking a link or clicking the dimmed page closes it (Esc is in Index).
export default function MenuPanel({ groups, active, onSelect, onClose }: Props) {
  const side = groups.filter((g) => !g.main)
  const main = groups.filter((g) => g.main)
  return (
    <>
      <div className="absolute inset-x-0 bottom-0 top-11 z-20 bg-foreground/25 animate-in fade-in duration-150" onClick={onClose} />
      <div className="absolute inset-x-0 top-11 z-30 flex flex-wrap border-b border-border bg-background shadow-lg animate-in fade-in slide-in-from-top-2 duration-150">
        <div className="flex w-[250px] flex-shrink-0 flex-col gap-4 bg-muted/50 p-4">
          {side.map((g) => (
            <div key={g.heading || "start"} className="flex flex-col gap-0.5">
              {g.heading && (
                <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase text-muted-foreground" style={{ letterSpacing: "1.2px" }}>
                  {g.heading}
                </div>
              )}
              {g.pages.map((p) => <PageLink key={p.key} page={p} active={p.key === active} onSelect={onSelect} />)}
            </div>
          ))}
        </div>

        {main.map((g) => (
          <div key={g.heading} className="flex min-w-[220px] flex-1 flex-col gap-0.5 p-4">
            <MainHeading group={g} />
            {g.pages.map((p) => <PageLink key={p.key} page={p} active={p.key === active} onSelect={onSelect} />)}
          </div>
        ))}
      </div>
    </>
  )
}

// Planning, Purchase, Push: icon in a tinted square, name, and what the part is for.
function MainHeading({ group }: { group: NavGroup }) {
  const Icon = group.main!
  return (
    <div className="mb-2 flex items-start gap-2.5 border-b border-border px-2.5 pb-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon size={17} strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-5 text-foreground">{group.heading}</div>
        <div className="text-xs leading-4 text-muted-foreground">{group.blurb}</div>
      </div>
    </div>
  )
}

function PageLink({ page, active, onSelect }: { page: NavPage; active: boolean; onSelect: (page: NavPage) => void }) {
  const Icon = page.icon
  return (
    <button
      type="button"
      onClick={() => onSelect(page)}
      aria-current={active ? "page" : undefined}
      className={`group flex items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${
        active ? "bg-primary/10" : "hover:bg-muted"
      }`}
    >
      {/* Same width as the heading's icon square, so icons share a center
          line and labels start where the heading name does. */}
      <span className="flex h-5 w-8 flex-shrink-0 items-center justify-center">
        <Icon size={16} strokeWidth={2} className={active ? "text-primary" : "text-muted-foreground group-hover:text-foreground"} />
      </span>
      <span className="min-w-0">
        <span className={`block text-sm leading-5 ${active ? "font-semibold text-primary" : "font-medium text-foreground"}`}>{page.label}</span>
        <span className="block text-xs leading-4 text-muted-foreground">{page.blurb}</span>
      </span>
    </button>
  )
}
